import React, { useCallback, useEffect, useState } from 'react';
import { useExercisesApi } from '../../app/api/useExercisesApi';
import { ApiExercise } from '@codey2/codey-api-types/models';
import Button from '../../components/common/Button/Button';
import { PlatformTag } from '../../components/exercises/PlatformTag/PlatformTag';
import styles from './ExerciseOverviewPage.module.css';
import { AddIcon } from '../../components/common/icons/addIcon';
import { EditIcon } from '../../components/common/icons/editIcon';
import { useAuth } from '../../app/auth/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../navigation/snackbar';
import { IconButton } from '../../components/common/IconButton/IconButton';
import { DeleteIcon } from '../../components/common/icons/deleteIcon';

export const ExerciseOverviewPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { getExercises, deleteExercise, isLoading } = useExercisesApi();
  const [exercises, setExercises] = useState<ApiExercise[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { showSnackbar } = useSnackbar();

  const loadExercises = useCallback(
    async (page: number) => {
      try {
        const response = await getExercises(page);
        if (page === 0) {
          setExercises(response.content || []);
        } else {
          setExercises(prev => [...prev, ...(response.content || [])]);
        }
        setTotalPages(response.totalPages || 0);
        setCurrentPage(page);
      } catch {
        showSnackbar('De opdrachten konden niet worden geladen', 'error', {
          action: { name: 'Opnieuw proberen', callback: () => loadExercises(page) },
        });
      }
    },
    [getExercises, showSnackbar]
  );

  useEffect(() => {
    loadExercises(0);
  }, [loadExercises]);

  const handleShowMore = () => {
    loadExercises(currentPage + 1);
  };

  const hasMorePages = currentPage + 1 < totalPages;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Opdrachten</h1>
          <p className={styles.subtitle}>Ontdek alle beschikbare opdrachten</p>
        </div>

        {isAuthenticated && (
          <div className={styles.actions}>
            <Link to="/opdrachten/genereren">
              <Button type="secondary">
                <AddIcon className={styles.addIcon} /> Opdracht genereren
              </Button>
            </Link>
            <Link to="/opdrachten/nieuw">
              <Button type="secondary">
                <AddIcon className={styles.addIcon} /> Opdracht toevoegen
              </Button>
            </Link>
          </div>
        )}
      </div>

      {exercises.length === 0 && !isLoading ? (
        <div className={styles.emptyState}>
          <p>Geen opdrachten gevonden</p>
        </div>
      ) : (
        <>
          <div className={styles.cardsGrid}>
            {exercises.map(exercise => {
              const isOwner = isAuthenticated && user?.username === exercise.owner;

              return (
                <Link key={exercise.id} to={`/opdrachten/${exercise.id}/poging`} className={styles.cardLink}>
                  <div className={styles.card}>
                    <div className={styles.cardHeader}>
                      <h3 className={styles.cardTitle}>{exercise.title}</h3>
                      <PlatformTag platform={exercise.platform} />
                    </div>
                    <p className={styles.cardIntroduction}>{exercise.introduction}</p>
                    <div className={styles.cardFooter}>
                      <span className={styles.owner}>Door: {exercise.owner}</span>
                      {isOwner && (
                        <div className={styles.ownerActions}>
                          <div
                            onClick={e => {
                              e.preventDefault();
                              navigate(`/opdrachten/${exercise.id}`);
                            }}
                          >
                            <IconButton icon={EditIcon} />
                          </div>
                          <div
                            onClick={async e => {
                              e.preventDefault();
                              await deleteExercise(exercise.id);
                              loadExercises(0);
                            }}
                          >
                            <IconButton icon={DeleteIcon} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {hasMorePages && (
            <div className={styles.loadMoreContainer}>
              <Button type="primary" onClick={handleShowMore} disabled={isLoading}>
                {isLoading ? 'Laden...' : 'Toon meer'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
